/**
 * Turning a generated level into something you can stand in.
 *
 * The compiler has already decided every coordinate; this only decides how it
 * looks. Materials, fog and light come from the theme, keyed by the semantic
 * role the compiler stamped on each solid — so the same geometry is a foundry
 * or an ice station depending on nothing but a palette.
 */

import * as THREE from 'three';
import { GUN_BASE, GUN_BY_FILE, gunLoader, markFamily, poseGun } from './gunpose.ts';
import type { GeneratedLevel, Theme } from 'levelspec/generate';
import type { Solid, SolidRole } from 'levelspec';
import { geometryForSolids, toThree } from '../three/adapter.ts';
import { buildScenery } from './scenery.ts';
import { buildDressing } from './dressing.ts';
import { derivePlaces, interiorFor } from './place.ts';
import { buildProps } from './props.ts';
import { accentFor, skyEnvironment, surfacesFor } from './materials.ts';
import type { FloorKind } from './materials.ts';
import { lightingOf, profileFor, type Lighting } from './art.ts';
import { bakeContact, Occupancy } from './shading.ts';
import { addReflections, type Reflections } from './reflect.ts';
import type { Tier } from './quality.ts';
import type { Collider } from '../three/player.ts';

/**
 * Free everything a subtree owns.
 *
 * The renderer keeps a GPU-side handle for every geometry and material until
 * somebody says otherwise, and a run is a hundred floors of somebody not
 * saying otherwise. Removing a group from the scene frees nothing at all: it
 * only stops it being drawn.
 *
 * Geometries and materials that are shared on purpose — a pooled enemy body,
 * the view model's brass — must not come through here, which is why it is
 * called on the world and on the pickups rather than on the whole scene.
 */
export function disposeTree(root: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    // A skeleton owns a texture of the bone matrices, and three makes one per
    // skeleton rather than per model. Nothing else frees it.
    (o as THREE.SkinnedMesh).skeleton?.dispose();
    if (m.geometry) geometries.add(m.geometry);
    const mat = (m as unknown as { material?: THREE.Material | THREE.Material[] }).material;
    if (Array.isArray(mat)) for (const one of mat) materials.add(one);
    else if (mat) materials.add(mat);
  });
  for (const g of geometries) g.dispose();
  for (const m of materials) {
    /*
     * A material's textures go with it — except the environment, which the
     * scene owns and every standard material merely points at. Freeing that
     * with the first material to be disposed would leave every other surface
     * in the game reflecting a dead texture.
     */
    for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'alphaMap']) {
      const tex = (m as unknown as Record<string, THREE.Texture | null>)[key];
      tex?.dispose();
    }
    m.dispose();
  }
}

/**
 * How big a shadow map each tier gets, or nothing at all.
 *
 * A phone renders the map once too, but a 2048 depth pass at load is a visible
 * hitch on a device that is already spending its whole budget on the first
 * frame, and the contact shading is what that tier is for.
 */
const SHADOW_MAP: Record<Tier, number> = { mobile: 0, balanced: 1024, high: 2048 };

export interface World {
  group: THREE.Group;
  colliders: Collider[];
  bounds: THREE.Box3;
  /** The one reflecting floor, on the tiers that have one. */
  reflections: Reflections | null;
  dispose(): void;
}

/**
 * The one thing the old per-solid tint was right about.
 *
 * A merged wall is one draw call and forty metres of identical colour, and
 * without something to break it you cannot see the corner you are walking
 * into. The answer was a hashed hue shift per solid, which is why no two
 * neighbouring panels were quite the same white and why the frame measured as
 * uniformly stained. Form should come from light, not from paint — so this is
 * flat white now, and the vertex channel is left free for the contact shading
 * that will actually carry the corners.
 */
const UNSHADED: [number, number, number] = [1, 1, 1];

/**
 * A little value between one wall and the next.
 *
 * Every `static_hard` and `exterior_wall` in the game shares one material at
 * one colour, so a room of eleven wall solids was eleven faces of exactly the
 * same grey and read as a single extruded surface rather than as built work.
 * Concrete does not do that: panels are poured at different times out of
 * different loads and they show it.
 *
 * It goes in the vertex-colour channel, which is already there for contact
 * shading and already multiplied through — so this is free, and no wall
 * becomes its own draw call. The spread is deliberately small and centred on
 * one: the frame gates weigh the shell mass against the graphite mass, and a
 * variation that drifted either way would move every map's numbers rather than
 * just its texture.
 */
const VARIED = new Set<SolidRole>(['static_hard', 'exterior_wall', 'soft_panel', 'lintel', 'sill']);
function panelTone(s: Solid): [number, number, number] {
  let h = 2166136261;
  const id = String(s.id);
  for (let i = 0; i < id.length; i++) { h ^= id.charCodeAt(i); h = Math.imul(h, 16777619); }
  // Narrow, and very slightly weighted downward: at plus or minus six per cent
  // a hard-morning map picked up a blown highlight it did not have before.
  const v = 0.95 + ((h >>> 9) & 1023) / 1023 * 0.09;
  return [v, v, v];
}

/**
 * A sky that is not one flat colour. Two triangles' worth of shader, drawn
 * behind everything with depth off — worth it because the horizon is what
 * tells you the level has an outside.
 */
function skyDome(lit: Lighting): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      top: { value: new THREE.Color(lit.zenith) },
      horizon: { value: new THREE.Color(lit.horizon) },
    },
    vertexShader: `
      varying float vH;
      void main() {
        vec4 world = modelMatrix * vec4(position, 1.0);
        vH = normalize(world.xyz - cameraPosition).y;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: `
      uniform vec3 top; uniform vec3 horizon;
      varying float vH;
      void main() {
        gl_FragColor = vec4(mix(horizon, top, clamp(pow(max(vH, 0.0), 0.55), 0.0, 1.0)), 1.0);
      }`,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  });
  const dome = new THREE.Mesh(new THREE.SphereGeometry(1, 24, 16), mat);
  dome.scale.setScalar(320);
  dome.renderOrder = -1;
  dome.frustumCulled = false;
  return dome;
}

/**
 * The light for a floor.
 *
 * One key, a sky term, and almost no ambient — and, on the tiers that can pay
 * for it, the key casts. That last part is not a detail. This comment used to
 * claim "one directional key with a real shadow map where the tier can afford
 * it" while the code enabled no shadows at all anywhere, which is most of why
 * a white room read as a white rectangle: with nothing occluding the key,
 * every surface facing it is the same value, and the only shape in the frame
 * comes from surface normals.
 *
 * In a world made of one pale material the projected shadow is frequently the
 * most important shape on screen. It is what tells you the atrium has a
 * gallery over it, where the light is coming in, and how big any of it is.
 */
export function applyTheme(
  scene: THREE.Scene,
  theme: Theme,
  renderer?: THREE.WebGLRenderer,
  mapId = '',
  bounds?: THREE.Box3,
  tier: Tier = 'balanced',
): THREE.Object3D[] {
  const lit = lightingOf(mapId);

  scene.background = new THREE.Color(lit.horizon);
  scene.fog = new THREE.Fog(lit.horizon, lit.fogNear, lit.fogFar);

  const dome = skyDome(lit);
  const hemi = new THREE.HemisphereLight(lit.skyColour, lit.groundColour, lit.hemiIntensity);
  const sun = new THREE.DirectionalLight(lit.sunColour, lit.sunIntensity);
  sun.position.set(lit.sun[0], lit.sun[1], lit.sun[2]);
  const ambient = new THREE.AmbientLight(0xdfe7f2, lit.ambient);

  /*
   * The shadow camera, fitted to the level and updated once.
   *
   * Nothing in the world moves, so the map is rendered a single time when the
   * floor loads and then never again — which is what makes a 2048 map
   * affordable on a machine that could not carry a shadow pass per frame. The
   * orthographic bounds are fitted to the level's own extent rather than to a
   * guess, because a shadow camera twice the size it needs to be is a shadow
   * map at a quarter of the resolution it looks like it has.
   */
  const size = SHADOW_MAP[tier];
  if (renderer && size && lit.shadowed && bounds && !bounds.isEmpty()) {
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = true;

    const centre = bounds.getCenter(new THREE.Vector3());
    const span = bounds.getSize(new THREE.Vector3());
    const reach = Math.max(span.x, span.z) * 0.62 + 12;
    sun.castShadow = true;
    sun.shadow.mapSize.set(size, size);
    sun.target.position.copy(centre);
    sun.position.copy(centre).add(
      new THREE.Vector3(lit.sun[0], lit.sun[1], lit.sun[2]).normalize().multiplyScalar(Math.max(90, reach * 1.7)),
    );
    const cam = sun.shadow.camera;
    cam.left = -reach;
    cam.right = reach;
    cam.top = reach;
    cam.bottom = -reach;
    cam.near = 1;
    cam.far = reach * 4 + 120;
    cam.updateProjectionMatrix();
    // Acne on a flat pale wall is a row of stripes across the one surface the
    // whole style depends on; the bias is generous because nothing here is
    // thin enough to lose its own contact shadow to it.
    sun.shadow.bias = -0.0009;
    sun.shadow.normalBias = 0.045;
    scene.add(sun.target);
  }

  scene.add(dome, hemi, sun, ambient);

  /*
   * Something for the polished surfaces to reflect.
   *
   * Built from the lighting profile rather than from the map's old palette, so
   * a resin floor reflects the sky this map is actually lit by. Set on the
   * scene, so one texture serves every standard material in the world.
   */
  if (renderer) {
    scene.environment?.dispose();
    scene.environment = skyEnvironment(renderer, lit.zenith, lit.horizon, lit.groundColour);
  }

  void theme;
  return [hemi, sun, sun.target, ambient, dome];
}

/**
 * How much of the machine this build is allowed to spend. Absent means all of
 * it, which is what the headless tools want.
 */
export interface WorldBudget {
  props: number;
  scenery: number;
  /** Which extras this machine is having. Absent means the headless default. */
  tier?: Tier;
}

export function buildWorld(gen: GeneratedLevel, quality?: WorldBudget): World {
  const group = new THREE.Group();
  const surfaces = surfacesFor(gen.spec.id);

  /*
   * What each room is for, before anything is drawn.
   *
   * It reads the space roles and the names the plan already carries — these
   * maps keep their areas' real names — and decides what kind of place each
   * one is. The props need it, and so does the ground: a floor inside a
   * building and the hardstanding of a yard are not the same surface, and the
   * only thing that knows which is which is this.
   */
  const places = derivePlaces(
    gen.spec,
    gen.level,
    interiorFor(gen.theme.facade, gen.theme.blocks, gen.theme.skyline),
    gen.spec.id,
  );
  /*
   * Which ground has sky over it.
   *
   * Asked of the geometry rather than of the room's name. A place's *use* says
   * what it is for, not whether it is covered — half these maps are traced
   * drafts with no roof line at all, where a space classified as a market or
   * an office is a rectangle of open ground with walls around it, and dressing
   * its floor as a polished interior is how a whole map comes out pale.
   *
   * So: stand on the middle of the slab and look up. If nothing is in the way
   * for twelve metres, it is outside, and outside the ground is hardstanding.
   *
   * And it has to be *ground*. A slab is painted from one test but seen from
   * two sides, and on a section map the top storey's floor is the storey
   * below's ceiling: open to the sky above, and thirty per cent of the frame
   * of an enclosed room beneath. Hardstanding on that underside is what made
   * an exchange's trading floor read 94% dark under a hundred per cent
   * enclosure — the darkest material in the palette, overhead, in a room lit
   * only by ambient. Hardstanding is the ground you stand on and nobody's
   * ceiling; a slab with a storey under it is resin from both sides, which is
   * mid, which is what a ceiling has to be.
   */
  const occupancy = new Occupancy(gen.level.solids);
  const floorKind = (s: Solid): FloorKind => {
    const x = (s.box.min[0] + s.box.max[0]) / 2;
    const z = -(s.box.min[1] + s.box.max[1]) / 2;
    // Down first, because it decides more. Anything solid a storey below means
    // the slab is a ceiling as well as a floor, and the test is asked of each
    // slab where it stands, so the half of a deck that sits over a basin is a
    // ceiling and the half that does not is still ground.
    const bottom = s.box.min[2];
    for (let down = 1; down < 8; down += 0.5) {
      if (occupancy.at(x, bottom - down, z)) return 'ceiling';
    }
    const top = s.box.max[2];
    for (let up = 0.6; up < 12; up += 0.5) {
      if (occupancy.at(x, top + up, z)) return 'inside';
    }
    return 'sky';
  };

  const buckets = new Map<string, Solid[]>();
  for (const s of gen.level.solids) {
    // Dynamic surfaces are the destructible ones; the run does not model
    // destruction yet, so they are drawn and collided like anything else.
    const k = `${s.layer}||${s.role}${s.role === 'floor' ? `||${floorKind(s)}` : ''}`;
    let arr = buckets.get(k);
    if (!arr) buckets.set(k, (arr = []));
    arr.push(s);
  }
  const disposables: (THREE.BufferGeometry | THREE.Material)[] = [];
  for (const [k, list] of buckets) {
    const parts = k.split('||');
    const role = parts[1] as SolidRole;
    const geo = geometryForSolids(list, VARIED.has(role) ? panelTone : () => UNSHADED);
    if (role !== 'glass') bakeContact(geo, occupancy);
    const mat = surfaces.get(role, (parts[2] as FloorKind) ?? 'inside');
    disposables.push(geo);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = k;
    mesh.renderOrder = role === 'glass' ? 2 : 0;
    // Both, on everything solid. A wall that receives but does not cast is a
    // wall the light goes through; one that casts but does not receive is a
    // wall nothing else can put a shadow on.
    mesh.castShadow = role !== 'glass';
    mesh.receiveShadow = true;
    group.add(mesh);
  }

  const colliders: Collider[] = gen.level.solids.map((s) => ({
    id: s.id,
    dynamic: s.dynamic,
    box: new THREE.Box3(
      new THREE.Vector3(s.box.min[0], s.box.min[2], -s.box.max[1]),
      new THREE.Vector3(s.box.max[0], s.box.max[2], -s.box.min[1]),
    ),
  }));
  const bounds = new THREE.Box3();
  for (const c of colliders) bounds.union(c.box);

  // The town around the street. Purely something to look at — it is outside
  // the footprint, carries no collider and is absent from the navmesh.
  group.add(
    buildScenery(gen.level, gen.spec.grid ?? 2, bounds, gen.theme, gen.spec.id, {
      skyline: gen.theme.skyline,
      facade: gen.theme.facade,
      roofs: gen.theme.roofs,
      blocks: gen.theme.blocks as never,
      // The map's one accent, not the theme's own. Two independent accent
      // colours in one frame is exactly the fault the curated set exists to
      // prevent, and the skyline is where the second one was coming from.
      accent: accentFor(gen.spec.id),
      /*
       * Day only if the look and the rig agree.
       *
       * The traced look carries a `daylight` flag off a screenshot and the art
       * profile carries the lighting rig that actually renders, and they can
       * disagree: a chlorine plant says daylight and is lit `clinical_night`.
       * Every window on every building in it was dark, on a map with no other
       * source of light in the frame at all — 0.3% of the picture pale against
       * the eight per cent a frame needs.
       */
      daylight: gen.theme.daylight && lightingOf(gen.spec.id).night !== true,
      detail: quality?.scenery,
    }),
  );
  const dressing = buildDressing(
    gen.level,
    gen.spec.id,
    gen.theme.facade,
    lightingOf(gen.spec.id).night === true,
    (s) => floorKind(s) === 'sky',
    lightingOf(gen.spec.id).night === true || profileFor(gen.spec.id).lighting === 'blue_hour',
  );
  dressing.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; }
  });
  group.add(dressing);

  const props = buildProps(gen.spec, gen.level, places, gen.spec.id, quality?.props);
  props.group.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) { m.castShadow = true; m.receiveShadow = true; }
  });
  group.add(props.group);
  /*
   * And the furniture becomes something you can walk into.
   *
   * Props were geometry and nothing else, so a vending machine was a picture
   * of one: you walked through it, and shots went through it. Marked `soft`
   * because the navmesh was baked from the level's own solids and has never
   * heard of any of this — the player and the bullets are stopped, and the
   * things that steer by the mesh are not, because a route the compiler drew
   * straight through a crate is a route an enemy has to be able to walk.
   */
  /*
   * And it stops you just inside the surface, not on it.
   *
   * The box is the exact bounds of what the fixture drew, and the resolver
   * adds the player's radius to every face — so a door leaf 160mm thick holds
   * you 860mm off it, and a 700mm one presents 1.4m of wall to the centre of a
   * body. The narrowest doorway in the library is 1.42m. That is twenty
   * millimetres of daylight, which is why a door you can plainly see past is a
   * door you have to go round the building to avoid.
   *
   * Six centimetres off each face is not visible against the geometry — you
   * are already a third of a metre away from it — and it is the difference
   * between edging past a fixture and finding another way round. Bounded to a
   * quarter of each span so nothing thin becomes something you walk through.
   */
  const INSET = 0.06;
  const inset = (lo: number, hi: number): [number, number] => {
    const give = Math.min(INSET, (hi - lo) * 0.25);
    return [lo + give, hi - give];
  };
  for (const b of props.solids) {
    const [x0, x1] = inset(b.x0, b.x1);
    const [z0, z1] = inset(b.z0, b.z1);
    colliders.push({
      id: `prop:${colliders.length}`,
      dynamic: false,
      soft: true,
      // Height is left alone: what it stands on and what it reaches to are
      // both things you can see, and neither is in the way sideways.
      box: new THREE.Box3(
        new THREE.Vector3(x0, b.y0, z0),
        new THREE.Vector3(x1, b.y1, z1),
      ),
    });
  }

  const reflections = quality?.tier ? addReflections(group, places, quality.tier) : null;

  return {
    group,
    colliders,
    bounds,
    reflections,
    dispose() {
      reflections?.dispose();
      for (const d of disposables) d.dispose();
      surfaces.dispose();
      props.dispose();
      // The level meshes were tracked; the scenery, the trim and the props
      // were not, and between them they are most of a floor.
      disposeTree(group);
      group.clear();
    },
  };
}

/**
 * The way out: unmissable, and the only bright thing in the room.
 *
 * Built at the origin and moved into place, because it does not exist until
 * the floor is clear and then appears wherever the player happens to be.
 */
export function buildExitMarker(): THREE.Group {
  /*
   * A doorway, not a searchlight.
   *
   * What was here was a torus, a five-metre translucent cylinder, a metre-wide
   * floor disc and a point light, all in the one bright green in the game —
   * which made the way out a column of fog visible through walls and the
   * single loudest thing on screen at all times. It also spent the whole of a
   * colour on a thing that is only interesting for the four seconds after a
   * floor is cleared.
   *
   * It is an architectural event now: a thin frame standing on the floor, a
   * pale sheet inside it, and a rectangle of light thrown at its feet. Unlit
   * and above the tone map, so it glows without needing to be large, and no
   * green anywhere in it — the way out is the map's own accent, which is the
   * colour you have been walking toward all floor.
   */
  const g = new THREE.Group();
  const colour = 0xdff1ff;
  const w = 1.15;
  const h = 2.3;
  const jamb = 0.06;

  const frame = new THREE.MeshBasicMaterial({ color: colour, toneMapped: false });
  const post = new THREE.BoxGeometry(jamb, h, jamb);
  for (const side of [-1, 1]) {
    const m = new THREE.Mesh(post, frame);
    m.position.set(side * w * 0.5, h / 2, 0);
    g.add(m);
  }
  const head = new THREE.Mesh(new THREE.BoxGeometry(w + jamb, jamb, jamb), frame);
  head.position.set(0, h, 0);
  g.add(head);

  // The opening itself: a sheet you can see is not a wall, dim enough that the
  // frame stays the brightest part of it.
  const sheet = new THREE.Mesh(
    new THREE.PlaneGeometry(w - jamb, h - jamb),
    new THREE.MeshBasicMaterial({
      color: colour, transparent: true, opacity: 0.3, side: THREE.DoubleSide,
      depthWrite: false, toneMapped: false, fog: false,
    }),
  );
  sheet.position.set(0, h / 2, 0);
  g.add(sheet);

  // Light on the floor in front of it, which is what a lit doorway does and
  // what tells you where it is from an angle the opening is edge-on to.
  const pool = new THREE.Mesh(
    new THREE.PlaneGeometry(w * 1.6, 1.5),
    new THREE.MeshBasicMaterial({
      color: colour, transparent: true, opacity: 0.16, depthWrite: false, toneMapped: false,
    }),
  );
  pool.position.set(0, 0.03, 0.75);
  pool.rotation.x = -Math.PI / 2;
  g.add(pool);

  /*
   * Dark until the way out exists, rather than absent.
   *
   * The number of lights the renderer can see is part of every material's
   * program key, so a light appearing invalidates every shader in the game —
   * and this one used to appear on the frame the last enemy died, which is
   * exactly the frame that should feel like a release. It is always in the
   * scene now and simply carries no energy until the exit opens.
   */
  const beacon = new THREE.PointLight(colour, 0, 9, 2);
  beacon.position.set(0, 1.4, 0.4);
  g.add(beacon);

  g.userData.spin = sheet;
  g.userData.beacon = beacon;
  return g;
}

/**
 * The signal you have to stand in.
 *
 * A ring on the floor and a soft column over it, in the map's own accent. It
 * has to be readable from across a room and from directly on top of it, which
 * is why it is both a boundary and a volume — the ring says where the edge is
 * and the column says there is something here at all.
 *
 * No light. There is no version of this that is worth recompiling every shader
 * in the game for, and the unlit materials carry themselves.
 */
export function buildSignal(colour: number, radius: number): THREE.Group {
  const g = new THREE.Group();

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(radius - 0.35, radius, 64),
    new THREE.MeshBasicMaterial({
      color: colour, transparent: true, opacity: 0.55, side: THREE.DoubleSide,
      depthWrite: false, toneMapped: false, fog: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.04;
  g.add(ring);

  // The fill is very faint: it marks the ground without hiding what is
  // standing on it, and what is standing on it is the reason you are here.
  const fill = new THREE.Mesh(
    new THREE.CircleGeometry(radius - 0.35, 48),
    new THREE.MeshBasicMaterial({
      color: colour, transparent: true, opacity: 0.07, depthWrite: false, toneMapped: false,
    }),
  );
  fill.rotation.x = -Math.PI / 2;
  fill.position.y = 0.03;
  g.add(fill);

  const column = new THREE.Mesh(
    new THREE.CylinderGeometry(radius * 0.92, radius * 0.92, 5, 32, 1, true),
    new THREE.MeshBasicMaterial({
      color: colour, transparent: true, opacity: 0.05, side: THREE.BackSide,
      depthWrite: false, toneMapped: false, fog: false,
    }),
  );
  column.position.y = 2.5;
  g.add(column);

  g.userData.ring = ring;
  return g;
}

/**
 * A weapon lying on the floor, as the actual gun.
 *
 * A crate with a colour on it tells you a weapon is there; the gun itself
 * tells you which one, from across the room, without reading anything. It is
 * the same model the view model uses, so what you pick up is what you saw.
 * The crate stays underneath as the thing you actually walk into and as the
 * stand-in until the model arrives.
 */
export function attachWeaponModel(group: THREE.Group, file: string, stripe: number): void {
  // The headless tools run the real Run against real maps, and a loader
  // pointed at an absolute path has nothing to resolve it against outside a
  // browser. Cosmetic either way, so it simply does not happen there.
  if (typeof document === 'undefined') return;
  const entry = GUN_BY_FILE.get(file);
  if (!entry) return;
  /*
   * A fresh parse per pickup, rather than one cached object cloned per use.
   *
   * These models are rigged, and cloning a rigged hierarchy is a minefield:
   * a plain clone leaves the skeleton unbound, and even a rebinding clone left
   * some meshes of a weapon drawn and others missing — a sniper rifle on the
   * floor showing its scope and nothing else. The GLB itself is already in the
   * browser cache, so re-parsing costs a few milliseconds for the two or three
   * weapons a floor puts on the ground, and every instance is independent and
   * whole.
   */
  const job = new Promise<THREE.Object3D | null>((resolve) => {
    gunLoader.load(
      GUN_BASE + file,
      (gltf) => resolve(gltf.scene),
      undefined,
      () => resolve(null),
    );
  });
  void job.then((src) => {
    if (!src || !group.parent) return;
    // The same measured pose the view model uses, so the gun on the floor is
    // the gun in your hands at the same size — which is the whole point of
    // showing it rather than a crate.
    const posed = poseGun(src, entry);
    posed.position.z = entry.length / 2;

    // Lay it flat and turn it, so it reads as dropped rather than displayed.
    const lay = new THREE.Group();
    lay.add(posed);
    lay.rotation.set(0, Math.PI * 0.17, Math.PI / 2);
    lay.position.y = 0.78;
    lay.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.frustumCulled = false;
      const src2 = Array.isArray(m.material) ? m.material : [m.material];
      const rebuilt = src2.map((one) => {
        const from = one as THREE.MeshStandardMaterial;
        return new THREE.MeshStandardMaterial({
          color: from?.color?.clone() ?? new THREE.Color(0x8a9099),
          map: from?.map ?? null,
          roughness: from?.roughness ?? 0.5,
          metalness: Math.min(0.6, from?.metalness ?? 0.35),
          emissive: new THREE.Color(0x2a2c30),
        });
      });
      m.material = Array.isArray(m.material) ? rebuilt : rebuilt[0];
    });
    /*
     * The class stripe last, so the rebuild above does not touch it.
     *
     * It is deliberately unlit — the same material the view model gives it —
     * and the pass that flattens these exports would turn it into a shaded
     * standard material like everything else. A gun on the floor of an unlit
     * room is exactly the case the stripe exists for, so it is the one thing
     * here that does not take the room's light.
     */
    markFamily(posed, stripe);
    group.add(lay);
    group.userData.spin = lay;
    // The crate was only ever a stand-in for this.
    const crate = group.userData.crate as THREE.Mesh | undefined;
    if (crate) crate.visible = false;
  });
}

/**
 * A floor pickup.
 *
 * A half-metre crate is invisible from across a room, and a player who has
 * never played one of these will not go looking under a railing for it. So it
 * gets a beam: the crate says what it is when you are close, the beam says
 * something is there from anywhere you can see the floor it sits on.
 */
export function buildPickup(kind: string, colour: number, at: [number, number, number]): THREE.Group {
  /*
   * A ring on the floor and a small bright thing above it.
   *
   * The beam this replaces was a three-and-a-half metre coloured tube, and
   * eleven of them down a lane turned the whole view the colour of whatever
   * you had not picked up yet — which is the same failure as colouring every
   * crate, in a brighter and more mobile form. A pickup does not need to be
   * seen through a wall; it needs to be unmistakable once you are looking at
   * the floor it is on.
   *
   * So: a thin ring where it stands, a small faceted marker turning above it,
   * and a glow that only exists near it. The gun model itself is added by the
   * caller and is the thing that actually tells you what it is.
   */
  const g = new THREE.Group();
  const p = toThree(at[0], at[1], at[2]);

  const marker = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.16, 0),
    new THREE.MeshBasicMaterial({ color: colour, toneMapped: false }),
  );
  marker.position.set(0, 0.85, 0);

  // A ring rather than a disc: a filled circle under a gun hides the gun.
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.42, 0.5, 24),
    new THREE.MeshBasicMaterial({
      color: colour, transparent: true, opacity: 0.55, depthWrite: false,
      side: THREE.DoubleSide, toneMapped: false,
    }),
  );
  ring.position.set(0, 0.03, 0);
  ring.rotation.x = -Math.PI / 2;

  /*
   * No light on a pickup.
   *
   * There was one each, and it was costing a full shader recompile of every
   * material in the scene twice per pickup: three.js keys its programs on the
   * number of lights it can see, so a light entering or leaving the scene
   * invalidates all of them. Eleven pickups dropping and being collected over
   * a floor is a stutter every time you walk onto one — which is exactly what
   * it felt like.
   *
   * Nothing is lost. The marker and the ring are unlit and drawn past the tone
   * map, so they are already the brightest things on the floor and already
   * bloom; the light was adding a soft pool underneath that nobody could pick
   * out from the ring above it.
   */
  g.add(marker, ring);
  g.position.set(p.x, p.y, p.z);
  g.userData.kind = kind;
  g.userData.spin = marker;
  g.userData.crate = marker;
  return g;
}
